/**
 * The write primitive. One in-flight call at a time, its own error, and no optimism.
 *
 * NOTHING HERE IS OPTIMISTIC, ON PURPOSE. Every write on this surface can be refused by a rule the
 * browser does not hold: `archiveOwnArticle` answers 409 if somebody moved the article mid-flight,
 * `unarchiveOwnArticle` answers 403 when staff did the archiving, `pullScheduledArticle` answers
 * 409 when go-live passed while the author was deciding. Painting the new state first and rolling
 * it back is how an author sees their article change status twice for one click. The screens
 * re-read after a success instead, which is also what decision-log :107 item 1 leaves to us: "the
 * platform's job is to expose state, not to direct the drawing".
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export interface ActionResult<TArgs extends readonly unknown[], TValue> {
  readonly run: (...args: TArgs) => Promise<TValue | undefined>;
  readonly isRunning: boolean;
  readonly error: unknown;
  readonly clearError: () => void;
}

export function useAction<TArgs extends readonly unknown[], TValue>(
  perform: (...args: TArgs) => Promise<TValue>,
): ActionResult<TArgs, TValue> {
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const performRef = useRef(perform);
  performRef.current = perform;
  // Guards the unmounted case: a reply that lands after the reader has navigated away must not
  // call setState on a dead component, and must not swallow the error either.
  const liveRef = useRef(true);
  // Set on every mount and cleared on every unmount - StrictMode re-mounts in development, and a
  // guard only ever set at creation would not survive that. Until 2026-09-23 nothing cleared it at
  // all, so it guarded nothing; `use-editor-draft.ts` had the opposite half and dropped saves.
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  const run = useCallback(async (...args: TArgs): Promise<TValue | undefined> => {
    setIsRunning(true);
    setError(undefined);
    try {
      const value = await performRef.current(...args);
      return value;
    } catch (cause) {
      if (liveRef.current) {
        setError(cause);
      }
      return undefined;
    } finally {
      if (liveRef.current) {
        setIsRunning(false);
      }
    }
  }, []);

  const clearError = useCallback(() => {
    setError(undefined);
  }, []);

  return { run, isRunning, error, clearError };
}
